import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { publicAPI, employerAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { 
  Building2, User, Briefcase, Calendar, Loader2, CheckCircle, 
  XCircle, AlertTriangle, Shield, FileText
} from 'lucide-react';

const PublicRecordRequest = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [request, setRequest] = useState(null);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [declineReason, setDeclineReason] = useState('');

  useEffect(() => {
    fetchRequest();
  }, [token]);

  const fetchRequest = async () => {
    try {
      const res = await publicAPI.getRecordRequest(token);
      setRequest(res.data);
    } catch (error) {
      if (error.response?.status === 404) {
        setError('This record request was not found or has been cancelled.');
      } else if (error.response?.status === 410) {
        setError('This record request has expired.');
      } else if (error.response?.status === 400) {
        setError(error.response.data.detail || 'This request has already been responded to.');
      } else {
        setError('Failed to load record request.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!user) {
      // Redirect to signup/login with return URL
      sessionStorage.setItem('vett_return_url', window.location.pathname);
      toast.info('Please create an employer account or sign in to accept this request.');
      navigate('/signup');
      return;
    }

    if (user.account_type !== 'employer') {
      toast.error('Only employer accounts can accept record requests.');
      return;
    }

    setAccepting(true);
    try {
      const res = await employerAPI.acceptRecordRequest(token);
      toast.success('Request accepted! You can now complete the record.');
      // Redirect to edit the draft record
      navigate(`/employer/records/${res.data.record_id}/edit`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept request');
    } finally {
      setAccepting(false);
    }
  };

  const handleDecline = async () => {
    if (!user) {
      sessionStorage.setItem('vett_return_url', window.location.pathname);
      toast.info('Please create an employer account or sign in to respond.');
      navigate('/signup');
      return;
    }

    if (user.account_type !== 'employer') {
      toast.error('Only employer accounts can decline record requests.');
      return;
    }

    setDeclining(true);
    try {
      await employerAPI.declineRecordRequest(token, declineReason);
      toast.success('Request declined.');
      setShowDeclineModal(false);
      setError('You have declined this request.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to decline request');
    } finally {
      setDeclining(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F8FF] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#1B5AFF]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F5F8FF] flex items-center justify-center p-4">
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-8 max-w-md w-full text-center">
          <AlertTriangle className="w-16 h-16 text-amber-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-[#0A0F1E] mb-2">Request Unavailable</h1>
          <p className="text-[#5B6472]">{error}</p>
          <Button
            onClick={() => navigate('/')}
            className="mt-6 bg-[#1B5AFF] hover:bg-[#1B5AFF]/90 text-white rounded-xl"
          >
            Go to VETT
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F8FF] py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Shield className="w-10 h-10 text-[#1B5AFF]" />
            <span className="text-3xl font-bold text-[#0A0F1E]">VETT</span>
          </div>
          <h1 className="text-2xl font-bold text-[#0A0F1E]">Employment Verification Request</h1>
          <p className="text-[#5B6472] mt-2">
            An employee is requesting verification of their employment at your company.
          </p>
        </div>

        {/* Request Details */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6 mb-6">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-16 h-16 rounded-xl bg-[#FFFFFF] flex items-center justify-center">
              <User className="w-8 h-8 text-[#1B5AFF]" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white">{request.employee_name}</h2>
              {request.employee_vett_id && (
                <p className="text-[#1B5AFF] font-mono text-sm">{request.employee_vett_id}</p>
              )}
              <p className="text-[#5B6472] text-sm mt-1">is requesting an employment record</p>
            </div>
          </div>

          <div className="space-y-4 border-t border-[#E8EDF5] pt-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-[#5B6472] text-xs uppercase tracking-wider mb-1">Company</p>
                <p className="text-white font-medium flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-[#5B6472]" />
                  {request.company_name}
                </p>
              </div>
              <div>
                <p className="text-[#5B6472] text-xs uppercase tracking-wider mb-1">Position</p>
                <p className="text-white font-medium flex items-center gap-2">
                  <Briefcase className="w-4 h-4 text-[#5B6472]" />
                  {request.job_title}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-[#5B6472] text-xs uppercase tracking-wider mb-1">Employment Period</p>
                <p className="text-white font-medium flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-[#5B6472]" />
                  {request.start_date} - {request.end_date || 'Present'}
                </p>
              </div>
              {request.department && (
                <div>
                  <p className="text-[#5B6472] text-xs uppercase tracking-wider mb-1">Department</p>
                  <p className="text-white font-medium">{request.department}</p>
                </div>
              )}
            </div>

            {request.employment_type && (
              <div>
                <p className="text-[#5B6472] text-xs uppercase tracking-wider mb-1">Employment Type</p>
                <p className="text-white font-medium">{request.employment_type}</p>
              </div>
            )}

            {request.custom_message && (
              <div className="bg-[#FFFFFF]/50 rounded-xl p-4 mt-4">
                <p className="text-[#5B6472] text-xs uppercase tracking-wider mb-2">Message from {request.employee_name}</p>
                <p className="text-[#0A0F1E] italic">"{request.custom_message}"</p>
              </div>
            )}
          </div>
        </Card>

        {/* What is VETT */}
        <Card className="bg-[#FFFFFF]/30 border border-[#E8EDF5] rounded-xl p-6 mb-6">
          <div className="flex items-start gap-3">
            <FileText className="w-6 h-6 text-[#1B5AFF] mt-0.5" />
            <div>
              <h3 className="text-white font-medium mb-2">What is a VETT Record?</h3>
              <p className="text-[#5B6472] text-sm">
                VETT records are verified employment records that help professionals build trusted work histories. 
                By creating a record, you're helping verify this person's employment while maintaining control over 
                what information is shared. Creating records is free and helps build trust in professional networking.
              </p>
            </div>
          </div>
        </Card>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Button
            onClick={handleAccept}
            disabled={accepting}
            className="flex-1 bg-[#1B5AFF] hover:bg-[#1B5AFF]/90 text-white rounded-xl py-6 text-lg"
          >
            {accepting ? (
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            ) : (
              <CheckCircle className="w-5 h-5 mr-2" />
            )}
            Accept & Create Record
          </Button>
          <Button
            onClick={() => setShowDeclineModal(true)}
            variant="outline"
            className="flex-1 border-[#E8EDF5] text-[#0A0F1E] hover:bg-[#FFFFFF] rounded-xl py-6 text-lg"
          >
            <XCircle className="w-5 h-5 mr-2" />
            Decline Request
          </Button>
        </div>

        {!user && (
          <p className="text-[#5B6472] text-sm text-center mt-4">
            You'll need to create a free employer account or sign in to respond.
          </p>
        )}
      </div>

      {/* Decline Modal */}
      <Dialog open={showDeclineModal} onOpenChange={setShowDeclineModal}>
        <DialogContent className="bg-[#FFFFFF] border-[#E8EDF5] text-white">
          <DialogHeader>
            <DialogTitle>Decline Record Request</DialogTitle>
          </DialogHeader>
          <p className="text-[#5B6472]">
            Are you sure you want to decline this request? The employee will be notified.
          </p>
          <div className="space-y-2 mt-4">
            <p className="text-[#0A0F1E] text-sm">Reason (optional)</p>
            <Textarea
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              placeholder="e.g., I don't have records for this employee, incorrect dates, etc."
              className="bg-[#FFFFFF]/50 border-[#E8EDF5] text-white rounded-xl"
            />
          </div>
          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => setShowDeclineModal(false)}
              className="border-[#E8EDF5] text-[#0A0F1E]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDecline}
              disabled={declining}
              className="bg-red-500 hover:bg-red-600 text-white rounded-xl"
            >
              {declining ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Decline Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PublicRecordRequest;
